import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import {
  PageHeading,
  Tabs,
  CardBalance,
  Pills,
  Button,
  Section,
  Empty,
  Text,
  Icon,
  Tooltip,
} from 'barte-design-system';
import { LockIcon } from '@phosphor-icons/react';
import {
  BolsaoStatusPill,
  useBolsaoAjudaIcon,
  BolsaoComoFunciona,
  SALDO_A_LIBERAR_AJUDA,
  SALDO_LIBERADO_AJUDA,
  type BolsaoStatus,
} from '../bolsao/bolsao';
import './DetalheLojista.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type TabKey =
  | 'visao-geral'
  | 'socios'
  | 'financeiro'
  | 'garantias'
  | 'taxas'
  | 'relatorios'
  | 'parceiros'
  | 'integracao';

// ─── Mock data ───────────────────────────────────────────────────────────────

const LOJISTA = {
  nome: 'Loja da Maria Moda',
  cnpj: '43.812.905/0001-27',
  razaoSocial: 'MARIA MODA COMERCIO DE VESTUARIO LTDA',
  nomeFantasia: 'Loja da Maria Moda',
  nomeResponsavel: 'Maria Aparecida Fontes',
  dataNascimento: '14/03/1987',
  dataAbertura: '02/08/2019',
  categoria: 'VESTUARIO',
  site: 'https://lojadamariamoda.com.br',
  cpfResponsavel: '318.774.260-15',
  documentosInternacionais: 'Não',
  cnaePrincipal: '4781-4/00',
  tier: 'Ativo sem antecipação',
};

const BOLSAO_RETIDO = 2450;
/** Teto opcional na config: null = sem teto (trava integral). */
const BOLSAO_TETO: number | null = 4000;
const BOLSAO_PERCENTUAL_TPV = '2,0% do TPV';
/** Nao existe montante esperado: o alvo do bolsao e o proprio teto. */
const BOLSAO_FALTA_PARA_TETO =
  BOLSAO_TETO === null ? null : Math.max(BOLSAO_TETO - BOLSAO_RETIDO, 0);

/** Estado exibido neste protótipo. */
const BOLSAO_STATUS_ATUAL: BolsaoStatus = 'retendo';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DetalheLojista() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('visao-geral');

  const temBolsao = true;

  const bolsaoIcon = useBolsaoAjudaIcon(BOLSAO_TETO);

  // ── Faixa de saldo (aba Visão geral) ──────────────────────────────────────

  const faixaDeSaldo = (
    <div className="dl-cards-row" data-cards={temBolsao ? 3 : 2}>
      <div className="dl-card-wrapper">
        <CardBalance className="dl-card dl-card--disponivel">
          <CardBalance.Info
            variant="big"
            title="Disponível para saque"
            balance={formatBRL(40000)}
            showIcon={false}
            showPills
            pills={
              <Tooltip label={SALDO_LIBERADO_AJUDA} placement="top">
                <span className="dl-pill-tooltip">
                  <Pills label="Saldo liberado" state="success" variant="light" size="sm" />
                </span>
              </Tooltip>
            }
            rightSlot={
              <div className="card-action">
                <Button variant="fill" size="sm" round="full" onClick={() => undefined}>
                  Solicitar saque
                </Button>
              </div>
            }
          />
        </CardBalance>
      </div>

      <div className="dl-card-wrapper">
        <CardBalance className="dl-card dl-card--futuros">
          <CardBalance.Info
            variant="big"
            title="Lançamentos futuros"
            balance={formatBRL(120000)}
            showIcon={false}
            showPills
            pills={
              <Tooltip label={SALDO_A_LIBERAR_AJUDA} placement="top">
                <span className="dl-pill-tooltip">
                  <Pills label="Saldo a liberar" state="accent" variant="light" size="sm" />
                </span>
              </Tooltip>
            }
          />
        </CardBalance>
      </div>

      {temBolsao && (
        <div className="dl-card-wrapper">
          <CardBalance className="dl-card dl-card--bolsao">
            <CardBalance.Info
              variant="big"
              title="Bolsão"
              balance={formatBRL(BOLSAO_RETIDO)}
              icon={bolsaoIcon}
              showPills
              pills={
                <BolsaoStatusPill status={BOLSAO_STATUS_ATUAL} size="sm" />
              }
              rightSlot={
                <div className="card-action">
                  <Button
                    variant="outline"
                    size="sm"
                    round="full"
                    onClick={() => setActiveTab('garantias')}
                  >
                    Ver detalhes
                  </Button>
                </div>
              }
            />
          </CardBalance>
        </div>
      )}
    </div>
  );

  // ── Aba Visão geral ───────────────────────────────────────────────────────

  const abaVisaoGeral = (
    <>
      {faixaDeSaldo}

      <Section
        title="Informações da empresa"
        variant="two-columns"
        headerActions={
          <Button variant="outline" size="sm" onClick={() => undefined}>
            Editar
          </Button>
        }
        secondColumn={
          <>
            <Section.Row label="Data de abertura" content={LOJISTA.dataAbertura} />
            <Section.Row label="Categoria" content={LOJISTA.categoria} />
            <Section.Row label="Site" content={LOJISTA.site} />
            <Section.Row label="CPF responsável" content={LOJISTA.cpfResponsavel} />
            <Section.Row
              label="Permitir documentos internacionais"
              content={LOJISTA.documentosInternacionais}
            />
            <Section.Row label="CNAE principal" content={LOJISTA.cnaePrincipal} />
          </>
        }
      >
        <Section.Row label="CNPJ" content={LOJISTA.cnpj} />
        <Section.Row label="Razão social" content={LOJISTA.razaoSocial} />
        <Section.Row label="Nome fantasia" content={LOJISTA.nomeFantasia} />
        <Section.Row label="Nome responsável" content={LOJISTA.nomeResponsavel} />
        <Section.Row label="Data de nascimento" content={LOJISTA.dataNascimento} />
      </Section>

      <Section
        title="Tier"
        headerActions={
          <Button variant="outline" size="sm" onClick={() => undefined}>
            Editar
          </Button>
        }
      >
        <Section.Row label="Tier" content={LOJISTA.tier} />
      </Section>
    </>
  );

  // ── Aba Garantias ─────────────────────────────────────────────────────────

  const cardBolsao = (
    <CardBalance className="dl-card dl-card--detalhe">
      <div className="bolsao-metrics">
        <div className="bolsao-metric">
          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
            % TPV
          </Text>
          <Text variant="body-lg" style={{ fontWeight: 600 }}>
            {BOLSAO_PERCENTUAL_TPV}
          </Text>
        </div>
        <div className="bolsao-metric">
          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
            Teto
          </Text>
          <Text variant="body-lg" style={{ fontWeight: 600 }}>
            {BOLSAO_TETO === null ? 'Sem teto' : `R$ ${formatBRL(BOLSAO_TETO)}`}
          </Text>
        </div>
        <div className="bolsao-metrics-divider" />
        <div className="bolsao-metric">
          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
            Status
          </Text>
          <div className="bolsao-metric-pill">
            <BolsaoStatusPill status={BOLSAO_STATUS_ATUAL} size="base" />
          </div>
        </div>
        <div className="bolsao-metric">
          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
            Retido
          </Text>
          <Text variant="body-lg" style={{ fontWeight: 600 }}>
            R$ {formatBRL(BOLSAO_RETIDO)}
          </Text>
        </div>
        {/* Sem teto ou teto ja atingido: o campo nao acrescenta nada e sai do card. */}
        {BOLSAO_FALTA_PARA_TETO !== null && BOLSAO_FALTA_PARA_TETO > 0 && (
          <div className="bolsao-metric">
            <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
              Falta para o teto
            </Text>
            <Text variant="body-lg" style={{ fontWeight: 600 }}>
              R$ {formatBRL(BOLSAO_FALTA_PARA_TETO)}
            </Text>
          </div>
        )}
      </div>
    </CardBalance>
  );

  const abaGarantias = (
    <div className="garantias-tab">
      <div className="garantias-header">
        <div className="garantias-titulo">
          <Icon icon={LockIcon} size={18} />
          <Text variant="body-lg" style={{ fontWeight: 600 }}>
            Bolsão
          </Text>
        </div>
        <Button variant="outline" size="sm" onClick={() => undefined}>
          Editar configuração
        </Button>
      </div>

      {temBolsao ? (
        <>
          {cardBolsao}
          <BolsaoComoFunciona teto={BOLSAO_TETO} />
        </>
      ) : (
        <Empty
          description={'Este lojista não possui bolsão.'}
          buttonLabel="Configurar bolsão"
          onRetry={() => undefined}
        />
      )}
    </div>
  );

  // ── Abas fora do escopo deste protótipo ───────────────────────────────────

  const abaForaDoEscopo = (
    <Empty description={'Conteúdo fora do escopo deste protótipo.'} showButton={false} />
  );

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'visao-geral', label: 'Visão geral' },
    { key: 'socios', label: 'Sócios' },
    { key: 'financeiro', label: 'Financeiro' },
    { key: 'garantias', label: 'Garantias' },
    { key: 'taxas', label: 'Taxas' },
    { key: 'relatorios', label: 'Relatórios' },
    { key: 'parceiros', label: 'Parceiros' },
    { key: 'integracao', label: 'Integração' },
  ];

  return (
    <PortalPayfacsLayout activeItem="detalhe-lojista">
      <main className="content">
        <div className="content-inner detalhe-lojista">
          <PageHeading title={LOJISTA.nome} onBack={() => navigate(-1)} />

          <Tabs>
            {TABS.map((tab) => (
              <Tabs.Tab
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs>

          {activeTab === 'visao-geral' && abaVisaoGeral}
          {activeTab === 'garantias' && abaGarantias}
          {activeTab !== 'visao-geral' && activeTab !== 'garantias' && abaForaDoEscopo}
        </div>
      </main>
    </PortalPayfacsLayout>
  );
}
